import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'lib/queryClient';
import { useAuthStore } from 'stores/authStore';
import ToastContainer from 'components/ui/Toast';

// Lazy-loaded pages
const InscriptionPage = React.lazy(() => import('pages/public/InscriptionPage'));
const StripeReturnPage = React.lazy(() => import('pages/public/StripeReturnPage'));
const LoginPage = React.lazy(() => import('pages/auth/LoginPage'));
const DashboardPage = React.lazy(() => import('pages/admin/DashboardPage'));
const InscriptionsPage = React.lazy(() => import('pages/admin/InscriptionsPage'));
const InscriptionDetailPage = React.lazy(() => import('pages/admin/InscriptionDetailPage'));
const SettingsPage = React.lazy(() => import('pages/admin/SettingsPage'));
const ManualDossierPage = React.lazy(() => import('pages/admin/ManualDossierPage'));
const TemplatesPage = React.lazy(() => import('pages/admin/TemplatesPage'));
const AuditTrailPage = React.lazy(() => import('pages/admin/AuditTrailPage'));
const LoginHistoryPage = React.lazy(() => import('pages/admin/LoginHistoryPage'));
const ActiveSessionsPage = React.lazy(() => import('pages/admin/ActiveSessionsPage'));
const PortailConnexionsPage = React.lazy(() => import('pages/admin/PortailConnexionsPage'));
const VerificationsPage = React.lazy(() => import('pages/admin/VerificationsPage'));
const ElevesPage = React.lazy(() => import('pages/admin/ElevesPage'));
const EleveGestionPage = React.lazy(() => import('pages/admin/EleveGestionPage'));
const AAffecterPage = React.lazy(() => import('pages/admin/AAffecterPage'));
const AffectationWorkspacePage = React.lazy(() => import('pages/admin/AffectationWorkspacePage'));
const ClassesPage = React.lazy(() => import('pages/admin/ClassesPage'));
const ClasseDetailPage = React.lazy(() => import('pages/admin/ClasseDetailPage'));
const EnseignantsPage = React.lazy(() => import('pages/admin/EnseignantsPage'));
const DefraiementsPage = React.lazy(() => import('pages/admin/DefraiementsPage'));
const AbsencesPage = React.lazy(() => import('pages/admin/AbsencesPage'));
const ReferencielPage = React.lazy(() => import('pages/admin/ReferencielPage'));
const PlanningPage = React.lazy(() => import('pages/admin/PlanningPage'));
const AffectationsListePage = React.lazy(() => import('pages/admin/AffectationsListePage'));
const ToolsPage = React.lazy(() => import('pages/admin/ToolsPage'));
const AiAssistantPage = React.lazy(() => import('pages/admin/AiAssistantPage'));
const CalendrierPage = React.lazy(() => import('pages/admin/CalendrierPage'));
const AnnoncesPage = React.lazy(() => import('pages/admin/AnnoncesPage'));
const AdminMessagesPage = React.lazy(() => import('pages/admin/AdminMessagesPage'));
const CampagnesPage = React.lazy(() => import('pages/admin/CampagnesPage'));
const CampagnesEnseignantsPage = React.lazy(() => import('pages/admin/CampagnesEnseignantsPage'));
const TawassolPage = React.lazy(() => import('pages/admin/TawassolPage'));
const AdminDevoirsPage = React.lazy(() => import('pages/admin/AdminDevoirsPage'));
const VieScolaireClassesPage = React.lazy(() => import('pages/admin/VieScolaireClassesPage'));
const VieScolaireAbsencesPage = React.lazy(() => import('pages/admin/VieScolaireAbsencesPage'));
const AujourdhuiPage = React.lazy(() => import('pages/admin/AujourdhuiPage'));
const HifzConfigPage = React.lazy(() => import('pages/admin/HifzConfigPage'));
const AdminHifzPage = React.lazy(() => import('pages/admin/AdminHifzPage'));
const AnnulationsPage = React.lazy(() => import('pages/admin/AnnulationsPage'));
const EngagementsPage = React.lazy(() => import('pages/admin/EngagementsPage'));
const AdminDocumentsPage = React.lazy(() => import('pages/admin/AdminDocumentsPage'));
const ComptabilitePage = React.lazy(() => import('pages/admin/ComptabilitePage'));

// Layout & guard
const AdminLayout = React.lazy(() => import('components/admin/AdminLayout'));
const ProtectedRoute = React.lazy(() => import('components/admin/ProtectedRoute'));

import { UserRole } from 'types/user.types';

function TitleUpdater() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = pathname.startsWith('/admin') ? 'Tilmid Admin' : 'Tilmid';
  }, [pathname]);
  return null;
}

function AppLoader({ children }: { children: React.ReactNode }) {
  return (
    <React.Suspense
      fallback={
        <div className="min-h-screen bg-surface flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      }
    >
      {children}
    </React.Suspense>
  );
}

export default function App() {
  // Restaure la session au démarrage via le cookie httpOnly de refresh
  useEffect(() => {
    useAuthStore.getState().bootstrap();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TitleUpdater />
        <AppLoader>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Navigate to="/inscription" replace />} />
            <Route path="/inscription" element={<InscriptionPage />} />
            <Route path="/inscription/succes" element={<StripeReturnPage />} />
            <Route path="/inscription/annule" element={<StripeReturnPage />} />

            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />

            {/* Admin */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<Navigate to="/admin/aujourdhui" replace />} />
                <Route path="/admin/aujourdhui" element={<AujourdhuiPage />} />
                <Route path="/admin/dashboard" element={<DashboardPage />} />
                <Route path="/admin/inscriptions" element={<InscriptionsPage />} />
                <Route path="/admin/inscriptions/:id" element={<InscriptionDetailPage />} />
                <Route path="/admin/saisie-manuelle" element={<ManualDossierPage />} />
                <Route path="/admin/eleves" element={<ElevesPage />} />
                <Route path="/admin/eleves/:eleveId/gestion" element={<EleveGestionPage />} />
                <Route path="/admin/a-affecter" element={<AAffecterPage />} />
                <Route path="/admin/a-affecter/:eleveId" element={<AffectationWorkspacePage />} />
                <Route path="/admin/classes" element={<ClassesPage />} />
                <Route path="/admin/classes/:id" element={<ClasseDetailPage />} />
                <Route path="/admin/planning" element={<PlanningPage />} />
                <Route path="/admin/affectations" element={<AffectationsListePage />} />
                <Route path="/admin/enseignants" element={<EnseignantsPage />} />
                <Route path="/admin/defraiements" element={<DefraiementsPage />} />
                <Route path="/admin/absences" element={<AbsencesPage />} />
                <Route path="/admin/referentiel" element={<ReferencielPage />} />
                <Route path="/admin/outils" element={<ToolsPage />} />
                <Route path="/admin/assistant" element={<AiAssistantPage />} />
                <Route path="/admin/calendrier" element={<CalendrierPage />} />
                <Route path="/admin/annonces" element={<AnnoncesPage />} />
                <Route path="/admin/tawassol" element={<TawassolPage />}>
                  <Route index element={<Navigate to="messages" replace />} />
                  <Route path="messages" element={<AdminMessagesPage />} />
                  <Route path="campagnes" element={<CampagnesPage />} />
                  <Route path="enseignants" element={<CampagnesEnseignantsPage />} />
                  <Route path="templates" element={<TemplatesPage />} />
                </Route>
                <Route path="/admin/travaux" element={<AdminDevoirsPage />} />
                <Route path="/admin/vie-scolaire/classes" element={<VieScolaireClassesPage />} />
                <Route path="/admin/vie-scolaire/absences" element={<VieScolaireAbsencesPage />} />
                <Route path="/admin/hifz" element={<HifzConfigPage />} />
                <Route path="/admin/suivi-hifz" element={<AdminHifzPage />} />
                <Route path="/admin/annulations" element={<AnnulationsPage />} />
                <Route path="/admin/engagements" element={<EngagementsPage />} />
                <Route path="/admin/documents" element={<AdminDocumentsPage />} />
                <Route path="/admin/comptabilite" element={<ComptabilitePage />} />
                <Route path="/admin/comptabilite/ecritures" element={<ComptabilitePage />} />
                <Route path="/admin/comptabilite/documents" element={<ComptabilitePage />} />
                <Route path="/admin/comptabilite/cheques" element={<ComptabilitePage />} />
                <Route path="/admin/comptabilite/activites" element={<ComptabilitePage />} />
                <Route path="/admin/comptabilite/configuration" element={<ComptabilitePage />} />
              </Route>
            </Route>

            {/* Super Admin */}
            <Route element={<ProtectedRoute roles={[UserRole.SUPER_ADMIN]} />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin/settings" element={<SettingsPage />} />
                <Route path="/admin/audit" element={<AuditTrailPage />} />
                <Route path="/admin/connexions" element={<LoginHistoryPage />} />
                <Route path="/admin/connexions-portail" element={<PortailConnexionsPage />} />
                <Route path="/admin/sessions" element={<ActiveSessionsPage />} />
                <Route path="/admin/verifications" element={<VerificationsPage />} />
              </Route>
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/inscription" replace />} />
          </Routes>
        </AppLoader>
        <ToastContainer />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
